var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import React from 'react';
import moize from 'moize';
import PropTypes from 'prop-types';
import { zIndex } from 'styled-system';
import styled from 'styled-components';
import { Flex, Box } from '..';
/**
 * Returns an array of values with the same length as numChildren with each item
 * set to the corresponding value in the variation string. If numChildren is greater than
 * the number of digits in variation, the same variation values will be repeated.
 * @param variationWidth - variation string
 * @param numChildren - number of children
 * @returns
 */
var getNonhomogeneousWidths = function (variationWidth, numChildren) {
    var variationWidths = variationWidth.split('-').map(function (width) { return parseInt(width, 10); });
    var widths = [];
    for (var i = 0; i < numChildren; i++) {
        widths.push(variationWidths[i % variationWidths.length] / 100);
    }
    return widths;
};
var getWidthsForVariation = function (variation, numChildren) {
    if (!variation) {
        return null;
    }
    if (variation === '50-50') {
        return Array(numChildren).fill(1 / 2);
    }
    else if (variation === '33-33-33') {
        return Array(numChildren).fill(1 / 3);
    }
    else if (variation === '25-25-25-25') {
        return Array(numChildren).fill(1 / 4);
    }
    else if (variation !== '100') {
        return getNonhomogeneousWidths(variation, numChildren);
    }
    // For when variation is 100
    return Array(numChildren).fill(1);
};
/**
 * Returns an array of responsive arrays of numbers with same length as numChildren. Each value represents the
 * width of the corresponding child.
 * @param variation - variation string to determine how items are sized
 * @param numChildren - number of children
 * @returns
 */
var getChildrenWidths = function (variation, numChildren) {
    if (Array.isArray(variation)) {
        var variationWidths = variation.map(function (v) {
            return getWidthsForVariation(v, numChildren);
        });
        var widthsPerChild = [];
        var _loop_1 = function (i) {
            var widths = variationWidths.map(function (v) { return v && v[i]; });
            widthsPerChild.push(widths);
        };
        for (var i = 0; i < numChildren; i++) {
            _loop_1(i);
        }
        return widthsPerChild;
    }
    else {
        return getWidthsForVariation(variation, numChildren);
    }
};
var memoGetChildrenWidths = moize(getChildrenWidths, { profileName: 'getChildrenWidths' });
// Map named sizes to responsive size values from theme
var gapValues = {
    sm: 1,
    md: 2,
    lg: 3,
    xl: 4,
};
/**
 * Returns an object containing responsive arrays of values for mx to apply to
 * the wrapper flex and px values to apply to the children.
 * @param gapProp - value passed in as gap prop
 * @returns
 */
var getGapValues = function (gapProp, rowGapProp) {
    var boxPaddingX;
    var boxPaddingY;
    var flexMarginX;
    var flexMarginY;
    if (Array.isArray(gapProp)) {
        boxPaddingX = gapProp.map(function (gap) { return gapValues[gap]; });
        // These values are negative because we're trying to offset the padding
        // applied to the left of the first item and right of the last item.
        flexMarginX = gapProp.map(function (gap) { return (gapValues[gap] ? -1 * gapValues[gap] : null); });
    }
    else {
        boxPaddingX = gapValues[gapProp];
        flexMarginX = -1 * gapValues[gapProp];
    }
    if (Array.isArray(rowGapProp)) {
        boxPaddingY = rowGapProp.map(function (gap) { return gapValues[gap] || null; });
        flexMarginY = rowGapProp.map(function (gap) { return (gapValues[gap] ? -1 * gapValues[gap] : null); });
    }
    else {
        boxPaddingY = gapValues[rowGapProp];
        flexMarginY = -1 * gapValues[rowGapProp];
    }
    return { boxPaddingX: boxPaddingX, boxPaddingY: boxPaddingY, flexMarginX: flexMarginX, flexMarginY: flexMarginY };
};
var memoGetGapValues = moize(getGapValues, { profileName: 'getGapValues' });
var ALLOWED_LAYOUT_VALUES = ['50-50', '33-33-33', '33-66', '66-33', '25-25-25-25', '60-40', '40-60', '100'];
var ALLOWED_GAP_VALUES = ['sm', 'md', 'lg', 'xl'];
var propTypes = {
    /** Children will have their widths set based on `variation` */
    children: PropTypes.node.isRequired,
    /** Configure widths of children at different breakpoints. Numbers in each variation
     * represent the width of the element in that position as a percentage of `Layout`'s width.
     */
    variation: PropTypes.oneOfType([
        PropTypes.oneOf(ALLOWED_LAYOUT_VALUES),
        PropTypes.arrayOf(PropTypes.oneOf(ALLOWED_LAYOUT_VALUES)),
    ]).isRequired,
    /** Add space between columns */
    gap: PropTypes.oneOfType([
        PropTypes.oneOf(ALLOWED_GAP_VALUES),
        PropTypes.arrayOf(PropTypes.oneOf(ALLOWED_GAP_VALUES)),
    ]),
    /** Add space between rows */
    rowGap: PropTypes.oneOfType([
        PropTypes.oneOf(ALLOWED_GAP_VALUES),
        PropTypes.arrayOf(PropTypes.oneOf(ALLOWED_GAP_VALUES)),
    ]),
    /** Add space between rows */
    stretchHeight: PropTypes.bool,
};
var ZIndexBox = styled(Box)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), zIndex);
var Layout = function (_a) {
    var children = _a.children, gap = _a.gap, rowGap = _a.rowGap, variation = _a.variation, stretchHeight = _a.stretchHeight;
    var widths = memoGetChildrenWidths(variation, children.length);
    var _b = memoGetGapValues(gap, rowGap), boxPaddingX = _b.boxPaddingX, boxPaddingY = _b.boxPaddingY, flexMarginX = _b.flexMarginX, flexMarginY = _b.flexMarginY;
    return (React.createElement(Flex, { flexWrap: 'wrap', mx: flexMarginX, my: flexMarginY, "data-testid": 'layout-flex' }, React.Children.map(children, function (child, idx) {
        return child && (React.createElement(ZIndexBox, { width: widths[idx], px: boxPaddingX, py: boxPaddingY, "data-testid": "box-".concat(idx), zIndex: child.props.zIndex }, React.cloneElement(child, {
            minHeight: stretchHeight ? '100%' : undefined,
        })));
    })));
};
Layout.propTypes = propTypes;
Layout.defaultProps = {};
Layout.displayName = 'Layout';
export default Layout;
var templateObject_1;
//# sourceMappingURL=Layout.js.map